import {
  Modal,
  Table,
  ScrollArea,
  Title,
  Stack,
  NativeSelect,
  Button,
  Group,
} from '@mantine/core';
import { useIntl } from 'react-intl';
import { useState, useEffect } from 'react';
import { IconDownload } from '@tabler/icons-react';
import * as XLSX from 'xlsx';

interface InfiniteListItemsModalProps {
  opened: boolean;
  onClose: () => void;
  file: any;
}

export const InfiniteListItemsModal = ({
  opened,
  onClose,
  file,
}: InfiniteListItemsModalProps) => {
  const intl = useIntl();

  // Get all infinite-list fields that have values
  const infiniteListFields = (file.fields || []).filter(
    (field: any) =>
      field.type === 'infinite-list' &&
      field.value &&
      Array.isArray(field.value) &&
      field.value.length > 0
  );

  const [selectedFieldId, setSelectedFieldId] = useState<string>('');

  // Set default selected field when modal opens or fields change
  useEffect(() => {
    if (infiniteListFields.length > 0 && !selectedFieldId) {
      setSelectedFieldId(infiniteListFields[0].id);
    }
  }, [infiniteListFields, selectedFieldId]);

  // Reset selected field when modal closes
  useEffect(() => {
    if (!opened) {
      setSelectedFieldId('');
    }
  }, [opened]);

  const selectedField = infiniteListFields.find(
    (field: any) => field.id === selectedFieldId
  );

  const downloadAsExcel = () => {
    if (!selectedField) return;

    // Get all inputs/columns
    const inputs = selectedField.inputs || [];
    const headers = [
      '#',
      ...inputs.map((input: any) => input.label || input.name),
    ];

    // Get all entries (filtered)
    const entries = selectedField.value || [];

    // Prepare data for Excel
    const excelData = [
      headers,
      ...entries.map((entry: any, index: number) => {
        return [
          index + 1,
          ...inputs.map((input: any) => {
            const value = entry[input.name];
            return getDisplayValue(input, value);
          }),
        ];
      }),
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths (optional - auto-width)
    const maxWidths = headers.map((_, colIndex) => {
      const maxLength = Math.max(
        ...excelData.map((row) => {
          const cellValue = row[colIndex];
          return cellValue ? String(cellValue).length : 0;
        })
      );
      return { wch: Math.min(Math.max(maxLength, 10), 50) };
    });
    ws['!cols'] = maxWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    // Generate Excel file and download
    const fileName = `${selectedField.label || 'infinite-list'}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const getDisplayValue = (input: any, value: any) => {
    if (input.type === 'signature') {
      return ![null, ''].includes(value) ? '✓' : '✗';
    } else if (input.type === 'datepicker') {
      return value
        ? new Date(value).toLocaleDateString('ro-RO', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
          })
        : '';
    } else if (input.type === 'imagePicker') {
      return Array.isArray(value) ? value.join(', ') : '';
    } else if (input.type === 'checkboxGroup') {
      return Array.isArray(value)
        ? (input.items || [])
            .filter((v: any) => value.includes(v.id))
            .map((v: any) => v.label)
            .join(', ')
        : '';
    }
    return value || '';
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={intl.formatMessage({
        id: 'infinite-list',
        defaultMessage: 'Infinite List Items',
      })}
      size="xl"
      centered
    >
      <Stack gap="md">
        <Group justify="space-between" align="flex-end">
          {infiniteListFields.length > 1 && (
            <NativeSelect
              label={intl.formatMessage({
                id: 'selectField',
                defaultMessage: 'Select Field',
              })}
              value={selectedFieldId}
              onChange={(e) => setSelectedFieldId(e.currentTarget.value)}
              data={infiniteListFields.map((field: any) => ({
                value: field.id,
                label: field.label,
              }))}
              style={{ flex: 1 }}
            />
          )}
          {selectedField && (
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={downloadAsExcel}
              variant="light"
            >
              {intl.formatMessage({
                id: 'downloadExcel',
                defaultMessage: 'Download Excel',
              })}
            </Button>
          )}
        </Group>

        {selectedField && (
          <Stack gap="xs">
            {infiniteListFields.length === 1 && (
              <Title order={5}>{selectedField.label}</Title>
            )}
            <ScrollArea>
              <Table
                striped
                highlightOnHover
                withTableBorder
                withColumnBorders
                layout="auto"
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    {(selectedField.inputs || []).map((input: any) => (
                      <Table.Th key={input.name || input.label}>
                        {input.label || input.name}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {selectedField.value.map((entry: any, index: number) => (
                    <Table.Tr key={index}>
                      <Table.Td>{index + 1}</Table.Td>
                      {(selectedField.inputs || []).map((input: any) => (
                        <Table.Td key={input.name || input.label}>
                          {getDisplayValue(input, entry[input.name])}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Stack>
        )}

        {infiniteListFields.length === 0 && (
          <div>
            {intl.formatMessage({
              id: 'noFieldsAvailable',
              defaultMessage: 'No infinite list fields with values',
            })}
          </div>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            {intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
